import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { PolygonCrop } from '@/components/projection/PolygonCrop';
import { FrameWithPolygon } from '@/components/ui/frame-with-polygon';
import { useFrameTime } from '@/components/ui/hover-time';
import { useT } from '@/contexts/I18nContext';

export interface LiveTableProof {
  id: string | number;
  name: string;
  cropSrc: string | null;
  cropAt?: string | null;
  frameSrc: string | null;
  frameAt?: string | null;
  polygons?: number[][][] | null;
  confirmedLabel: string;
  observedAt?: string | null;
  pending?: boolean;
}

interface TableImage {
  kind: 'frame' | 'frameCrop' | 'crop';
  src: string;
  capturedAt?: string | null;
  polygons: number[][][];
}

/** The displayed image and its timestamp are committed together after decoding. */
export function useTableProof(source: LiveTableProof | undefined, mode: 'frame' | 'crop') {
  const id = source ? String(source.id) : undefined;
  const cropSrc = source?.cropSrc;
  const cropAt = source?.cropAt;
  const frameSrc = source?.frameSrc;
  const frameAt = source?.frameAt;
  const polygons = source?.polygons;
  const [displayed, setDisplayed] = useState<{ id: string | number; image: TableImage | null; loading: boolean } | null>(null);
  useEffect(() => {
    if (id === undefined) return;
    let active = true;
    let probe: HTMLImageElement | null = null;
    const candidates: TableImage[] = [];
    const preferFrame = mode === 'frame' || !cropSrc || !cropAt || !frameAt || Date.parse(frameAt) >= Date.parse(cropAt);
    if (frameSrc && polygons?.length && preferFrame) {
      candidates.push({ kind: mode === 'frame' ? 'frame' : 'frameCrop', src: frameSrc, capturedAt: frameAt, polygons });
    }
    if (cropSrc) candidates.push({ kind: 'crop', src: cropSrc, capturedAt: cropAt, polygons: [] });
    setDisplayed(previous => ({ id, image: previous?.id === id ? previous.image : null, loading: true }));
    void (async () => {
      for (const candidate of candidates) {
        const loaded = await new Promise<boolean>(resolve => {
          const image = new Image();
          probe = image;
          image.onload = () => resolve(true);
          image.onerror = () => resolve(false);
          image.src = candidate.src;
          if (image.complete && image.naturalWidth) resolve(true);
        });
        if (!active) return;
        if (loaded) {
          setDisplayed({ id, image: candidate, loading: false });
          return;
        }
      }
      if (active) setDisplayed({ id, image: null, loading: false });
    })();
    return () => {
      active = false;
      if (probe) { probe.onload = null; probe.onerror = null; }
    };
  }, [id, cropSrc, cropAt, frameSrc, frameAt, polygons, mode]);
  return displayed && displayed.id === id ? displayed : { image: null, loading: id !== undefined };
}

export function TableProofImage({ image, loading, name, onAspect }: {
  image: TableImage | null;
  loading: boolean;
  name: string;
  onAspect?: (aspect: number, src: string) => void;
}) {
  const { t } = useT();
  if (!image) return loading
    ? <Loader2 className="absolute left-1/2 top-1/2 size-6 -translate-x-1/2 -translate-y-1/2 animate-spin" aria-label={t('common.loading')} />
    : <span className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">{t('tables.photoUnavailable')}</span>;
  const key = `${image.kind}:${image.src}:${image.capturedAt}`;
  if (image.kind === 'frame') return (
    <FrameWithPolygon key={key} src={image.src} polygons={image.polygons} alt={name} exactSource
      onAspect={aspect => onAspect?.(aspect, image.src)} />
  );
  if (image.kind === 'frameCrop') return (
    <PolygonCrop key={key} frameSrc={image.src} polygons={image.polygons} alt={name}
      className="absolute inset-0 size-full object-contain" onFrameAspect={aspect => onAspect?.(aspect, image.src)} />
  );
  return <img key={key} src={image.src} alt={name} draggable={false} className="absolute inset-0 size-full object-contain"
    onLoad={event => onAspect?.(event.currentTarget.naturalWidth / Math.max(1, event.currentTarget.naturalHeight), image.src)} />;
}

/** Include the date for older captures so yesterday's photo cannot look current. */
export function useTableProofTime() {
  const { lang } = useT();
  const frameTime = useFrameTime();
  return (iso: string | null | undefined): string => {
    const time = frameTime(iso);
    if (!iso || !time) return '';
    const options = { timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit' } as const;
    const day = new Date(iso).toLocaleDateString(lang, options);
    const today = new Date().toLocaleDateString(lang, options);
    return day === today ? time : `${day} ${time}`;
  };
}

export function TableConfirmedState({ source }: { source: LiveTableProof }) {
  const { t } = useT();
  return <span>{[source.confirmedLabel,
    source.pending ? t('tables.rechecking') : null,
  ].filter(Boolean).join(' · ')}</span>;
}
